import { graphqlClient } from '@/services/core/graphqlClient';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Phone Validation Service
 * Handles phone number validation using GraphQL API
 */
export class PhoneValidationService {
    /**
     * Check if phone numbers are invalid
     * @param numbers Array of phone numbers to validate
     * @param hasuraToken Authentication token
     * @returns Array of invalid phone numbers
     */
    async getInvalidPhoneNumbers(
        numbers: string[], 
        hasuraToken: string
    ): Promise<string[]> {
        // Filter out empty or null numbers
        const validNumbers = numbers.filter(num => 
            num && num.trim() !== '' && num !== 'n/a' && num !== 'N/A'
        ).map(num => num.replace(/\D/g, '')); // Remove non-digits

        if (validNumbers.length === 0) {
            return [];
        }

        const query = `
            query getInvalidPhoneNumbers($numbers: [String!]) {
                api_invalid_phone_number(where: {invalid_phone_number: {_in: $numbers}}) {
                    invalid_phone_number
                    __typename
                }
            }
        `;

        try {
            const result = await graphqlClient.query(
                query,
                { numbers: validNumbers },
                hasuraToken
            );

            return result.data?.api_invalid_phone_number?.map(
                (item: any) => item.invalid_phone_number
            ) || [];
        } catch (error) {
            console.error('Error validating phone numbers:', error);
            return [];
        }
    }

    /**
     * Check if a single phone number is valid
     * @param phoneNumber Phone number to validate
     * @param hasuraToken Authentication token
     * @returns true if valid, false if invalid
     */
    async isPhoneNumberValid(
        phoneNumber: string, 
        hasuraToken: string
    ): Promise<boolean> {
        if (!phoneNumber || phoneNumber.trim() === '' || phoneNumber === 'n/a' || phoneNumber === 'N/A') {
            return false;
        }

        const invalidNumbers = await this.getInvalidPhoneNumbers([phoneNumber], hasuraToken);
        const cleanNumber = phoneNumber.replace(/\D/g, '');
        return !invalidNumbers.includes(cleanNumber);
    }

    /**
     * Format phone number for display
     * @param phoneNumber Raw phone number
     * @returns Formatted phone number
     */
    formatPhoneNumber(phoneNumber: string): string {
        if (!phoneNumber) return '';
        
        const digits = phoneNumber.replace(/\D/g, '');
        
        if (digits.length === 10) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        
        return phoneNumber;
    }

    /**
     * Clean phone number (remove all non-digits)
     * @param phoneNumber Raw phone number
     * @returns Cleaned phone number
     */
    cleanPhoneNumber(phoneNumber: string): string {
        return phoneNumber ? phoneNumber.replace(/\D/g, '') : '';
    }
}

// Export singleton instance
export const phoneValidationService = new PhoneValidationService();

// Hook for using phone validation in components
export const usePhoneValidation = () => {
    const { hasuraToken } = useAuth();

    const validatePhoneNumbers = async (numbers: string[]) => {
        if (!hasuraToken) {
            console.warn('No hasura token available for phone validation');
            return [];
        }
        return await phoneValidationService.getInvalidPhoneNumbers(numbers, hasuraToken);
    };

    const isPhoneValid = async (phoneNumber: string) => {
        if (!hasuraToken) {
            console.warn('No hasura token available for phone validation');
            return true; // Default to valid if no token
        }
        return await phoneValidationService.isPhoneNumberValid(phoneNumber, hasuraToken);
    };

    return {
        validatePhoneNumbers,
        isPhoneValid,
        formatPhoneNumber: phoneValidationService.formatPhoneNumber,
        cleanPhoneNumber: phoneValidationService.cleanPhoneNumber
    };
};